import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { FIELD_TYPE_REGISTRY } from './registry'
import { parseConfig, parseOptions, resolveFieldValue } from './types'
import type { CustomFieldDefinitionDocument, CustomFieldValueDocument } from './types'
import { useCustomFieldValueMutations } from '@/lib/db/rxdb/hooks'

const METADATA_PILL_CLASSES =
  'inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 shadow-sm'
const METADATA_SIDEBAR_CLASSES = 'flex w-full items-center gap-2 py-1'

interface CustomFieldRendererProps {
  definition: CustomFieldDefinitionDocument
  value: CustomFieldValueDocument | undefined
  taskId: string
  readOnly?: boolean
  variant?: 'metadata' | 'section'
  layout?: 'horizontal' | 'sidebar'
}

export function CustomFieldRenderer({
  definition,
  value,
  taskId,
  readOnly = false,
  variant = 'section',
  layout = 'horizontal',
}: CustomFieldRendererProps) {
  const { createCustomFieldValue, updateCustomFieldValue } = useCustomFieldValueMutations()
  const entry = FIELD_TYPE_REGISTRY[definition.fieldType]
  const options = useMemo(() => parseOptions(definition.options), [definition.options])

  const currentValue = useMemo(() => {
    if (!value) return null
    switch (definition.fieldType) {
      case 'string':
      case 'list':
        return value.stringValue
      case 'number':
        return value.numberValue
      case 'date':
        return value.dateValue
      default:
        return null
    }
  }, [value, definition.fieldType])

  const handleChange = useCallback(
    async (newValue: string | number | null) => {
      if (newValue != null) {
        const error = entry.validateValue(newValue, parseConfig(definition.config))
        if (error) {
          toast.error(error)
          return
        }
      }

      const resolved = resolveFieldValue(definition.fieldType, newValue)

      if (value) {
        await updateCustomFieldValue(value.id, resolved)
      } else {
        await createCustomFieldValue({
          taskId,
          customFieldDefinitionId: definition.id,
          ...resolved,
        } as any)
      }
    },
    [
      entry,
      value,
      taskId,
      definition.id,
      definition.fieldType,
      definition.config,
      createCustomFieldValue,
      updateCustomFieldValue,
    ],
  )

  if (!entry) return null

  const { Editor } = entry

  if (variant === 'metadata') {
    const isSidebar = layout === 'sidebar'
    return (
      <div className={isSidebar ? METADATA_SIDEBAR_CLASSES : METADATA_PILL_CLASSES}>
        <label
          className={
            isSidebar
              ? 'shrink-0 text-sm text-sidebar-foreground'
              : 'shrink-0 text-sm font-medium text-foreground whitespace-nowrap'
          }
        >
          {definition.name}
          {definition.isRequired && <span className="ml-0.5 text-destructive">*</span>}
        </label>
        <div className={isSidebar ? 'min-w-0 flex-1' : 'w-32 min-w-0'}>
          <Editor
            definition={definition}
            value={currentValue}
            options={options}
            onChange={handleChange}
            readOnly={readOnly}
            compact
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-4">
      <label className="shrink-0 text-sm font-medium text-foreground">
        {definition.name}
        {definition.isRequired && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      <div className="min-w-0 flex-1">
        <Editor
          definition={definition}
          value={currentValue}
          options={options}
          onChange={handleChange}
          readOnly={readOnly}
        />
      </div>
    </div>
  )
}
